import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import {
  Send,
  Save,
  TestTube2,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Clock,
  ExternalLink,
  CreditCard,
  UserCog,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const BOT_META = {
  payment: {
    icon: CreditCard,
    accent: "emerald",
    tip: "Create a bot via @BotFather on Telegram → /newbot → copy the token. Add the bot to your ops group, then use @userinfobot to find the group's chat_id (starts with -100...).",
  },
  agent: {
    icon: UserCog,
    accent: "amber",
    tip: "A separate bot/group lets you route agent deposits & withdraw approvals to a different team.",
  },
  livechat: {
    icon: MessageSquare,
    accent: "sky",
    tip: "Get real-time pings for every incoming live-chat message so your support team never misses a visitor.",
  },
};

const ACCENT = {
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-600", ring: "peer-checked:bg-emerald-500", btn: "bg-emerald-500 hover:bg-emerald-600" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-600", ring: "peer-checked:bg-amber-500", btn: "bg-amber-500 hover:bg-amber-600" },
  sky: { bg: "bg-sky-500/10", text: "text-sky-600", ring: "peer-checked:bg-sky-500", btn: "bg-sky-500 hover:bg-sky-600" },
};

function BotCard({ bot, onSave, onTest }) {
  const meta = BOT_META[bot.key] || {};
  const accent = ACCENT[meta.accent] || ACCENT.emerald;
  const Icon = meta.icon || Send;

  const [form, setForm] = useState({
    enabled: !!bot.enabled,
    label: bot.label || "",
    bot_token: "", // leave blank → server keeps existing; user types to replace
    chat_id: bot.chat_id || "",
  });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      enabled: !!bot.enabled,
      label: bot.label || "",
      chat_id: bot.chat_id || "",
    }));
  }, [bot.enabled, bot.label, bot.chat_id, bot.updated_at]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!form.enabled,
        label: form.label,
        chat_id: (form.chat_id || "").trim(),
      };
      // Only send bot_token if the user actually typed a new value
      if (form.bot_token && form.bot_token.trim()) {
        payload.bot_token = form.bot_token.trim();
      }
      await onSave(bot.key, payload);
      setForm((f) => ({ ...f, bot_token: "" })); // clear the input
      setShowToken(false);
      toast.success(`${bot.label} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await onTest(bot.key);
      setTestResult(res);
      if (res.ok) toast.success("Test message sent ✅");
      else toast.error(res.error || "Test failed");
    } catch (e) {
      const err = e?.response?.data?.error || e?.message || "Test failed";
      setTestResult({ ok: false, error: err });
      toast.error(err);
    } finally {
      setTesting(false);
    }
  };

  const lastSent = bot.last_sent_at ? new Date(bot.last_sent_at).toLocaleString() : "—";
  const hasToken = !!bot.bot_token_set;
  const ready = hasToken && !!(bot.chat_id || "").trim();

  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`tg-bot-card-${bot.key}`}>
      {/* Header */}
      <div className="flex items-start gap-4 p-5 border-b border-gray-100 bg-gray-50/60">
        <div className={`h-11 w-11 rounded-sm inline-flex items-center justify-center shrink-0 ${accent.bg} ${accent.text}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-base font-semibold text-gray-900">{bot.label}</div>
            {bot.enabled ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-emerald-500/10 text-emerald-700 rounded-sm">
                <CheckCircle2 size={10} /> Enabled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-gray-200 text-gray-600 rounded-sm">
                Disabled
              </span>
            )}
            {ready ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-sky-500/10 text-sky-700 rounded-sm">
                Configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-amber-500/10 text-amber-700 rounded-sm">
                <AlertCircle size={10} /> Needs setup
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">{bot.description}</div>
          <div className="text-[11px] text-gray-500 mt-1.5 inline-flex items-center gap-1">
            <Clock size={11} /> Last sent: <span className="text-gray-700">{lastSent}</span>
            {bot.last_error ? (
              <span className="ml-2 text-red-600" title={bot.last_error}>
                · Last error: <span className="font-mono">{String(bot.last_error).slice(0, 60)}</span>
              </span>
            ) : null}
          </div>
        </div>
        <label className="inline-flex items-center cursor-pointer shrink-0 mt-1">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            className="sr-only peer"
            data-testid={`tg-enabled-${bot.key}`}
          />
          <span className={`relative w-11 h-6 bg-gray-300 rounded-full transition-colors ${accent.ring} after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5`}></span>
        </label>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Display label</label>
          <input
            value={form.label}
            onChange={(e) => set("label", e.target.value)}
            className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
            data-testid={`tg-label-${bot.key}`}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Bot token</label>
            <div className="relative">
              <input
                type={showToken ? "text" : "password"}
                value={form.bot_token}
                onChange={(e) => set("bot_token", e.target.value)}
                placeholder={hasToken ? bot.bot_token_masked || "••••••••" : "123456:ABC-DEF..."}
                className="w-full h-10 pl-3 pr-10 border border-gray-200 rounded-sm text-sm font-mono focus:outline-none focus:border-emerald-500"
                data-testid={`tg-token-${bot.key}`}
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                tabIndex={-1}
                aria-label={showToken ? "Hide token" : "Show token"}
              >
                {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              {hasToken
                ? "Leave blank to keep the existing token. Type a new value to replace it."
                : "Format: 123456789:AAHrZ... (from @BotFather)"}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Chat ID</label>
            <input
              value={form.chat_id}
              onChange={(e) => set("chat_id", e.target.value)}
              placeholder="-1001234567890  or  123456789"
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono focus:outline-none focus:border-emerald-500"
              data-testid={`tg-chatid-${bot.key}`}
              spellCheck={false}
            />
            <div className="text-[11px] text-gray-500 mt-1">
              Group chat (starts with <code>-100...</code>) or personal chat (positive integer).
            </div>
          </div>
        </div>

        {/* Tip */}
        <div className="flex items-start gap-2 p-3 rounded-sm border border-dashed border-gray-200 bg-gray-50 text-xs text-gray-600">
          <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
          <div>{meta.tip}</div>
        </div>

        {/* Test result */}
        {testResult && (
          <div
            className={`flex items-start gap-2 p-3 rounded-sm text-xs border ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid={`tg-test-result-${bot.key}`}
          >
            {testResult.ok ? <CheckCircle2 size={14} className="mt-0.5" /> : <AlertCircle size={14} className="mt-0.5" />}
            <div>
              <div className="font-semibold">{testResult.ok ? "Sent ✓" : "Failed"}</div>
              {testResult.ok ? (
                <div className="opacity-80">{testResult.message || "Check your Telegram chat for the test message."}</div>
              ) : (
                <div className="font-mono opacity-80 break-all">{testResult.error}</div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={runTest}
            disabled={testing || !bot.enabled || !ready}
            title={!ready ? "Save bot token + chat_id first" : !bot.enabled ? "Enable the bot first" : ""}
            className="h-10 px-4 border border-gray-200 hover:border-gray-400 text-gray-700 rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`tg-test-${bot.key}`}
          >
            <TestTube2 size={14} /> {testing ? "Sending..." : "Send test message"}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className={`h-10 px-5 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 ${accent.btn}`}
            data-testid={`tg-save-${bot.key}`}
          >
            <Save size={14} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ConfigTelegram() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/admin/telegram/bots");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (key, payload) => {
    const { data } = await api.put(`/site/admin/telegram/bots/${key}`, payload);
    setItems(data.items || []);
  };

  const handleTest = async (key) => {
    const { data } = await api.post(`/site/admin/telegram/bots/${key}/test`);
    return data;
  };

  return (
    <div data-testid="config-telegram-page">
      <PageHeader
        title="Configuration"
        subtitle="Telegram notifications · three independent bots for payments, agents and live-chat"
      />
      <ConfigTabs />

      {/* Intro banner */}
      <div className="bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-200 rounded-sm p-5 mb-5 flex items-start gap-4" data-testid="tg-intro">
        <div className="h-11 w-11 rounded-sm bg-sky-500/15 text-sky-700 inline-flex items-center justify-center shrink-0">
          <Send size={20} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-gray-900">Telegram Outbound Notifications</div>
          <div className="text-xs text-gray-600 mt-1 leading-relaxed">
            Each bot is independent — use a different Telegram bot + chat for each team if you like. Messages are
            best-effort and never block the underlying deposit / withdraw / chat flow. To create a bot: open Telegram, chat with{" "}
            <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-sky-700 hover:underline font-semibold inline-flex items-center gap-0.5">
              @BotFather <ExternalLink size={11} />
            </a>{" "}
            → <code>/newbot</code> → copy the token here. Then add the bot to your group and use{" "}
            <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-sky-700 hover:underline font-semibold inline-flex items-center gap-0.5">
              @userinfobot <ExternalLink size={11} />
            </a>{" "}
            to find the group's chat_id.
          </div>
        </div>
      </div>

      {loading || !items ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading bots…</div>
      ) : (
        <div className="space-y-5">
          {items.map((bot) => (
            <BotCard key={bot.key} bot={bot} onSave={handleSave} onTest={handleTest} />
          ))}
        </div>
      )}
    </div>
  );
}
